/**
 * Calculation utilities for the Closing Disclosure feature
 *
 * All calculation functions include boundary condition handling:
 * - Safe handling of null/undefined values
 * - Protection against division by zero
 * - Reasonable bounds checking for financial values
 * - NaN/Infinity protection
 */

import type { CalculatedTotals, LineItem, LoanTerms } from '../types';

// =============================================================================
// Safe Arithmetic Helpers
// =============================================================================

/**
 * Safely convert a value to a number, defaulting to 0 for invalid values
 */
export function toSafeNumber(value: number | null | undefined | string): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const num = typeof value === 'string' ? Number.parseFloat(value) : value;
  if (isNaN(num) || !isFinite(num)) {
    return 0;
  }
  return num;
}

/**
 * Safely add numbers, treating null/undefined as 0
 */
export function safeAdd(...values: (number | null | undefined)[]): number {
  return values.reduce<number>((sum, val) => sum + toSafeNumber(val), 0);
}

/**
 * Safely subtract numbers
 */
export function safeSubtract(
  minuend: number | null | undefined,
  ...subtrahends: (number | null | undefined)[]
): number {
  const base = toSafeNumber(minuend);
  const subtracted = subtrahends.reduce<number>((sum, val) => sum + toSafeNumber(val), 0);
  return base - subtracted;
}

/**
 * Safely multiply numbers
 */
export function safeMultiply(...values: (number | null | undefined)[]): number {
  if (values.length === 0) return 0;
  return values.reduce<number>((product, val) => product * toSafeNumber(val), 1);
}

/**
 * Safely divide numbers, returning 0 for division by zero
 */
export function safeDivide(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
  fallback = 0,
): number {
  const num = toSafeNumber(numerator);
  const den = toSafeNumber(denominator);
  if (den === 0) {
    return fallback;
  }
  const result = num / den;
  return isFinite(result) ? result : fallback;
}

/**
 * Round to specified decimal places with safe handling
 */
export function safeRound(value: number | null | undefined, decimals = 2): number {
  const num = toSafeNumber(value);
  const multiplier = Math.pow(10, decimals);
  return Math.round(num * multiplier) / multiplier;
}

/**
 * Clamp a value between min and max bounds
 */
export function clamp(value: number | null | undefined, min: number, max: number): number {
  const num = toSafeNumber(value);
  return Math.min(Math.max(num, min), max);
}

// =============================================================================
// Section Total Calculations
// =============================================================================

/**
 * Calculate the sum of borrower-paid amounts for a given section
 */
export function calculateSectionTotal(lineItems: LineItem[], section: string): number {
  return lineItems
    .filter((item) => item.section === section)
    .reduce((sum, item) => {
      const atClosing = item.borrowerPaidAtClosing || 0;
      const beforeClosing = item.borrowerPaidBeforeClosing || 0;
      return sum + atClosing + beforeClosing;
    }, 0);
}

/**
 * Calculate total loan costs (Section A + B + C)
 */
export function calculateTotalLoanCosts(lineItems: LineItem[]): number {
  const sectionA = calculateSectionTotal(lineItems, 'A');
  const sectionB = calculateSectionTotal(lineItems, 'B');
  const sectionC = calculateSectionTotal(lineItems, 'C');
  return sectionA + sectionB + sectionC;
}

/**
 * Calculate total other costs (Section E + F + G + H)
 */
export function calculateTotalOtherCosts(lineItems: LineItem[]): number {
  const sectionE = calculateSectionTotal(lineItems, 'E');
  const sectionF = calculateSectionTotal(lineItems, 'F');
  const sectionG = calculateSectionTotal(lineItems, 'G');
  const sectionH = calculateSectionTotal(lineItems, 'H');
  return sectionE + sectionF + sectionG + sectionH;
}

/**
 * Calculate total closing costs (D + I)
 */
export function calculateTotalClosingCosts(
  totalLoanCosts: number,
  totalOtherCosts: number,
): number {
  return totalLoanCosts + totalOtherCosts;
}

/**
 * Calculate lender credits from line items (paidByOthers)
 */
export function calculateLenderCredits(lineItems: LineItem[]): number {
  return lineItems.reduce((sum, item) => sum + (item.paidByOthers || 0), 0);
}

/**
 * Calculate seller credits from line items
 */
export function calculateSellerCredits(lineItems: LineItem[]): number {
  return lineItems.reduce((sum, item) => {
    const atClosing = item.sellerPaidAtClosing || 0;
    const beforeClosing = item.sellerPaidBeforeClosing || 0;
    return sum + atClosing + beforeClosing;
  }, 0);
}

/**
 * Calculate all totals for a transaction
 */
export function calculateAllTotals(
  lineItems: LineItem[],
  salePrice = 0,
  loanAmount = 0,
  depositAmount = 0,
): CalculatedTotals {
  const totalLoanCosts = calculateTotalLoanCosts(lineItems);
  const totalOtherCosts = calculateTotalOtherCosts(lineItems);
  const totalClosingCosts = calculateTotalClosingCosts(totalLoanCosts, totalOtherCosts);
  const lenderCredits = calculateLenderCredits(lineItems);
  const sellerCredits = calculateSellerCredits(lineItems);

  // Cash to close calculation (simplified)
  // Total Due from Borrower - Total Paid Already
  const totalDue = salePrice + totalClosingCosts;
  const totalPaid = loanAmount + depositAmount + lenderCredits + sellerCredits;
  const cashToClose = totalDue - totalPaid;

  return {
    totalLoanCosts,
    totalOtherCosts,
    totalClosingCosts,
    lenderCredits,
    sellerCredits,
    cashToClose,
  };
}

/**
 * Calculate monthly payment (principal and interest only)
 */
export function calculateMonthlyPayment(
  loanAmount: number,
  annualInterestRate: number,
  loanTermMonths: number,
): number {
  if (loanAmount <= 0 || loanTermMonths <= 0) {
    return 0;
  }

  if (annualInterestRate === 0) {
    return loanAmount / loanTermMonths;
  }

  const monthlyRate = annualInterestRate / 100 / 12;
  const numerator = loanAmount * monthlyRate * Math.pow(1 + monthlyRate, loanTermMonths);
  const denominator = Math.pow(1 + monthlyRate, loanTermMonths) - 1;

  return numerator / denominator;
}

/**
 * Calculate total interest over the life of the loan
 */
export function calculateTotalInterest(
  monthlyPayment: number,
  loanTermMonths: number,
  loanAmount: number,
): number {
  const totalPayments = monthlyPayment * loanTermMonths;
  return totalPayments - loanAmount;
}

/**
 * Calculate APR (Annual Percentage Rate)
 * This is a simplified calculation - actual APR calculation is more complex
 */
export function calculateAPR(
  loanAmount: number,
  totalInterest: number,
  prepaidFinanceCharges: number,
  loanTermMonths: number,
): number {
  if (loanAmount <= 0 || loanTermMonths <= 0) {
    return 0;
  }

  const totalCost = totalInterest + prepaidFinanceCharges;
  const loanTermYears = loanTermMonths / 12;
  const effectivePrincipal = loanAmount - prepaidFinanceCharges;

  if (effectivePrincipal <= 0) {
    return 0;
  }

  // Simplified APR calculation
  const apr = (totalCost / effectivePrincipal / loanTermYears) * 100;
  return Math.round(apr * 1000) / 1000;
}

/**
 * Calculate TIP (Total Interest Percentage)
 */
export function calculateTIP(totalInterest: number, loanAmount: number): number {
  if (loanAmount <= 0) {
    return 0;
  }

  const tip = (totalInterest / loanAmount) * 100;
  return Math.round(tip * 1000) / 1000;
}

/**
 * Calculate projected monthly escrow payment
 */
export function calculateMonthlyEscrow(
  annualPropertyTax: number,
  annualHomeownersInsurance: number,
  annualMortgageInsurance = 0,
  annualFloodInsurance = 0,
): number {
  const total =
    annualPropertyTax + annualHomeownersInsurance + annualMortgageInsurance + annualFloodInsurance;
  return total / 12;
}

/**
 * Calculate total monthly payment including escrow
 */
export function calculateTotalMonthlyPayment(
  principalAndInterest: number,
  monthlyEscrow: number,
  monthlyHOA = 0,
): number {
  return principalAndInterest + monthlyEscrow + monthlyHOA;
}

/**
 * Validate loan terms and return any warnings
 */
export function validateLoanTerms(
  loanTerms: Partial<LoanTerms>,
): { field: string; message: string }[] {
  const warnings: { field: string; message: string }[] = [];

  if (loanTerms.loan_amount && loanTerms.loan_amount <= 0) {
    warnings.push({
      field: 'loan_amount',
      message: 'Loan amount must be greater than zero',
    });
  }

  if (loanTerms.interest_rate && (loanTerms.interest_rate < 0 || loanTerms.interest_rate > 30)) {
    warnings.push({
      field: 'interest_rate',
      message: 'Interest rate should be between 0% and 30%',
    });
  }

  if (loanTerms.loan_term_months && loanTerms.loan_term_months <= 0) {
    warnings.push({
      field: 'loan_term_months',
      message: 'Loan term must be greater than zero months',
    });
  }

  return warnings;
}

/**
 * Calculate loan-to-value ratio
 */
export function calculateLTV(loanAmount: number, propertyValue: number): number {
  if (propertyValue <= 0) {
    return 0;
  }
  return (loanAmount / propertyValue) * 100;
}

/**
 * Calculate combined loan-to-value ratio
 */
export function calculateCLTV(
  primaryLoanAmount: number,
  secondaryLoanAmount: number,
  propertyValue: number,
): number {
  if (propertyValue <= 0) {
    return 0;
  }
  return ((primaryLoanAmount + secondaryLoanAmount) / propertyValue) * 100;
}
